import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import Dinero from 'dinero.js';
import sortBy from 'lodash/sortBy';
import { OrderContext } from '../../../context/OrderContext';
import { isDeliverMethod, isEmailValid, isRegularArticle } from '../../../utils/helpers';
import { CampaignContext } from '../../../context/CampaignWrapper';
import StripeCheckout from '../../../components/StripeCheckout';
import ICheckoutButton from '../interfaces/ICheckoutButton';
import TooltipWrapper from '../../../components/TooltipWrapper';
import useMediaQuery from '../../../utils/hooks/mediaQuery.hook';
import orderToAPI from '../../../utils/helpers/orderToApi';
import { DeliveryMethod, useSellerGroupsQuery } from '../../../generated/graphql';

const CheckoutButton = ({ isPrivacyAccepted } : ICheckoutButton) => {
  const { order, articleCount } = useContext(OrderContext);
  const { isMobile } = useMediaQuery();

  const { campaign } = useContext(CampaignContext);
  const {
    articles, minimumTotalArticleCount, minimumOrderValue, isSellerGroupRequired, deliveryOptions, formFields, timeslots,
    textConfig: { sellerGroupLabel }, slug,
  } = campaign;
  const { data: sellerGroupsData } = useSellerGroupsQuery({ skip: !slug, variables: { slug } });
  const [translate] = useTranslation();

  const { minimumTotalPrice = 0, deliveryMethods: unorderedDeliveryMethods = [] } = deliveryOptions || {};
  const deliveryMethods = sortBy(unorderedDeliveryMethods);
  const {
    name, email, deliveryMethod = deliveryMethods?.[0],
    quantities, customFields, sellerGroupId, address, collectTimeslot,
  } = order || {};
  const hasMultipleDeliveryMethods = (deliveryMethods || []).length > 1;
  const hasCollectDeliveryMethod = deliveryMethods?.includes(DeliveryMethod.Collect);
  const hasSellerGroups = !!sellerGroupsData?.campaignBySlug?.sellerGroups?.length;

  const totalPrice = articles.reduce((sum, { _id, price }) => sum + ((quantities[_id] || 0) * price), 0);

  const regularArticleIds = articles.filter(isRegularArticle).map((a) => a._id.toString());
  const regularQuantity = Object
    .entries(order.quantities)
    .reduce((total, [articleId, quantity]) => (regularArticleIds.includes(articleId) ? total + quantity : total), 0);
  const donationArticle = articles.find((a) => a.__typename === 'DonationArticle');
  const donationCount = donationArticle && order.quantities[donationArticle._id];
  const isDonationOnly = !!donationCount && Object.entries(order.quantities).length === 1;

  const getInvalidCustomField = () => {
    const hasRequiredFields = formFields.some((field) => field.isRequired);

    if (!hasRequiredFields) return '';

    return formFields.reduceRight((error, { _id, isRequired, label }) => {
      const selected = customFields[_id] || [];
      const invalidField = Array.isArray(selected) && (!selected.length || selected.some((val) => (`${val}`).length < 1));

      if (!isRequired) return error;
      if (!selected || invalidField) return label;

      return error;
    }, '');
  };

  const getValidationMessage = () => {
    if (!order) return translate('COMPONENTS.CHECKOUT_CONTAINER.CHECKOUT_BUTTON.NO_ORDER_FOUND');
    if (!name || name.length < 2) return translate('COMPONENTS.CHECKOUT_CONTAINER.CHECKOUT_BUTTON.ENTER_NAME');
    if (!email) return translate('COMPONENTS.CHECKOUT_CONTAINER.CHECKOUT_BUTTON.ENTER_MAIL_ADDRESS');

    if (!isEmailValid(email)) {
      return translate(
        'COMPONENTS.CHECKOUT_CONTAINER.CHECKOUT_BUTTON.NOT_VALID_EMAIL',
        { email },
      );
    }

    if (minimumTotalArticleCount && articleCount < minimumTotalArticleCount) {
      return translate(
        'COMPONENTS.CHECKOUT_CONTAINER.CHECKOUT_BUTTON.MINIMUM_ITEMS_PER_ORDER',
        { count: minimumTotalArticleCount },
      );
    }

    if (articleCount === 0 && !donationCount) {
      return translate('COMPONENTS.CHECKOUT_CONTAINER.CHECKOUT_BUTTON.INDICATE_ARTICLE');
    }

    if (regularQuantity === 0 && donationCount && donationCount < 1000) {
      return translate('COMPONENTS.CHECKOUT_CONTAINER.CHECKOUT_BUTTON.MINIMUM_DONATION');
    }

    if (totalPrice < minimumOrderValue) {
      return translate(
        'COMPONENTS.CHECKOUT_CONTAINER.CHECKOUT_BUTTON.MINIMUM_ORDER_VALUE',
        { value: Dinero({ amount: minimumOrderValue }).toFormat() },
      );
    }

    if (isSellerGroupRequired && !sellerGroupId && hasSellerGroups) {
      return translate(
        'COMPONENTS.CHECKOUT_CONTAINER.CHECKOUT_BUTTON.FILL_FIELD',
        { value: sellerGroupLabel || translate('COMPONENTS.CHECKOUT_CONTAINER.FORM.SUPPORT_VIA') },
      );
    }

    if (hasMultipleDeliveryMethods && !deliveryMethod && !isDonationOnly) {
      return translate('COMPONENTS.CHECKOUT_CONTAINER.CHECKOUT_BUTTON.CHOOSE_DELIVERY_METHOD');
    }

    if (isDeliverMethod(deliveryMethod)
        && (!address?.route || !address.streetNumber || !address.postcode || !address.locality)
        && !isDonationOnly) {
      return hasMultipleDeliveryMethods && hasCollectDeliveryMethod
        ? translate('COMPONENTS.CHECKOUT_CONTAINER.CHECKOUT_BUTTON.CHOOSE_DELIVERY_PICKUP')
        : translate('COMPONENTS.CHECKOUT_CONTAINER.CHECKOUT_BUTTON.ENTER_DELIVERY_ADDRESS');
    }

    if (deliveryMethod && (timeslots[deliveryMethod] || []).length && !collectTimeslot && !isDonationOnly) {
      return translate('COMPONENTS.CHECKOUT_CONTAINER.CHECKOUT_BUTTON.CHOOSE_TIMESLOT');
    }

    const invalidCustomField = getInvalidCustomField();

    if (invalidCustomField) {
      return translate(
        'COMPONENTS.CHECKOUT_CONTAINER.CHECKOUT_BUTTON.FILL_FIELD',
        { value: invalidCustomField },
      );
    }

    if (isDeliverMethod(deliveryMethod) && totalPrice < (minimumTotalPrice || 0)) {
      return translate(
        'COMPONENTS.CHECKOUT_CONTAINER.CHECKOUT_BUTTON.MIN_ORDER_VALIDATION',
        { minOrder: Dinero({ amount: minimumTotalPrice || 0 }).toFormat() },
      );
    }

    if (!isPrivacyAccepted) return translate('COMPONENTS.CHECKOUT_CONTAINER.CHECKOUT_BUTTON.PRIVACY_AGREE');

    return '';
  };

  const validationMessage = getValidationMessage();
  const isButtonDisabled = !!validationMessage;

  return (
    <>
      <TooltipWrapper tooltip={validationMessage}>
        <StripeCheckout
          order={orderToAPI(order, campaign, isDonationOnly)}
          isButtonDisabled={isButtonDisabled}
          buttonLabel={translate('COMPONENTS.CHECKOUT_CONTAINER.CHECKOUT_BUTTON.PROCEED_TO_PAYMENT')}
        />
      </TooltipWrapper>
      {isMobile && name?.length > 0 && email?.length > 0 && <p className="mt-3">{validationMessage}</p>}
    </>
  );
};

export default CheckoutButton;
